import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useT } from '../../i18n';
import { robotColorName } from '../../missionLabel';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  label: {
    fontFamily: MONO,
    color: '#c4a070',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  // Timer reads as a stamped gauge: recessed plate, monospace numerals.
  timerPlate: {
    borderRadius: '10px',
    background: '#150d07',
    border: '1px solid #4a3020',
    padding: '12px',
    textAlign: 'center',
    boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.6)',
  },
  timer: {
    fontFamily: MONO,
    fontSize: '34px',
    fontWeight: '700',
    lineHeight: 1,
    letterSpacing: '2px',
    color: ({ theme, urgent }) => urgent ? '#e35c36' : theme.colorText,
  },
  timerCaption: {
    marginTop: '4px',
    fontFamily: MONO,
    fontSize: '9.5px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: ({ urgent }) => urgent ? '#d94f2b' : '#9c8168',
  },
  // The mission's optimum, in competitive modes. Deliberately a quiet row
  // rather than a second gauge — it is a fixed fact about the round, not a
  // countdown, and must not compete with the timer for attention.
  optimalPlate: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    borderRadius: '8px',
    background: '#150d07',
    border: '1px solid #4a3020',
    padding: '7px 10px',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
  },
  optimalCaption: {
    fontFamily: MONO,
    fontSize: '9.5px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: '#9c8168',
  },
  optimalValue: {
    fontFamily: MONO,
    fontSize: '20px',
    fontWeight: '700',
    lineHeight: 1,
    color: '#e0a325',
  },
  // The hint reads as a fact about the round, like the optimum above it —
  // a recessed row, not a second button competing with the bid.
  hintPlate: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    borderRadius: '8px',
    background: '#150d07',
    border: '1px solid #4a3020',
    padding: '7px 10px',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
  },
  hintSwatch: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    flexShrink: 0,
    background: ({ hintColor }) => hintColor ?? 'transparent',
    boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.45)',
  },
  hintRobot: {
    fontFamily: MONO,
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: '#f0dcc0',
  },
  hintStep: {
    marginLeft: 'auto',
    fontFamily: MONO,
    fontSize: '9.5px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: '#9c8168',
  },
  bidList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  bidItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: '7px',
    background: '#1c130c',
    border: '1px solid #3a2718',
  },
  bidName: {
    flex: 1,
    fontSize: '14px',
    color: ({ theme }) => theme.colorText,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  bidCount: {
    fontFamily: MONO,
    fontSize: '14px',
    fontWeight: 700,
    color: ({ theme }) => theme.colorText,
    background: '#3a2718',
    borderRadius: '5px',
    padding: '2px 8px',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)',
  },
  empty: {
    fontFamily: MONO,
    fontSize: '11.5px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: '#9c8168',
    textAlign: 'center',
    padding: '10px',
  },
  sandboxBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 0',
    borderTop: '1px solid #4a3020',
    borderBottom: '1px solid #4a3020',
  },
  sandboxBtn: {
    width: '38px',
    height: '36px',
    flexShrink: 0,
    background: '#3a2718',
    border: '1px solid #533620',
    borderRadius: '7px',
    color: '#e8d6ae',
    cursor: 'pointer',
    fontSize: '15px',
    fontFamily: 'inherit',
    lineHeight: 1,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -2px 0 rgba(0,0,0,0.3)',
    '&:disabled': {
      opacity: 0.35,
      cursor: 'default',
      boxShadow: 'none',
    },
    '&:hover:not(:disabled)': {
      background: '#4a3220',
    },
    '&:active:not(:disabled)': {
      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)',
    },
  },
  movesDisplay: {
    flex: 1,
    minWidth: 0,
    height: '36px',
    boxSizing: 'border-box',
    background: '#150d07',
    border: '1px solid #4a3020',
    borderRadius: '7px',
    padding: '0 10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '6px',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
  },
  movesLabel: {
    fontFamily: MONO,
    color: '#9c8168',
    fontSize: '9.5px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },
  bidInput: {
    width: '48px',
    minWidth: 0,
    background: 'transparent',
    border: 'none',
    color: '#f5e6c8',
    fontFamily: MONO,
    fontSize: '16px',
    fontWeight: 700,
    textAlign: 'right',
    outline: 'none',
    padding: 0,
  },
  bidBtn: {
    height: '36px',
    flexShrink: 0,
    padding: '0 16px',
    background: '#d94f2b',
    border: '1px solid #120c07',
    borderRadius: '7px',
    color: '#fff3e6',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 700,
    fontFamily: 'inherit',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    lineHeight: 1,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), 0 2px 0 #7d2a12',
    transition: 'background .12s ease, transform .06s ease, box-shadow .06s ease',
    '&:disabled': {
      opacity: 0.35,
      cursor: 'default',
      boxShadow: 'none',
    },
    '&:hover:not(:disabled)': {
      background: '#e35c36',
    },
    '&:active:not(:disabled)': {
      transform: 'translateY(2px)',
      boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.4)',
    },
  },
  skipSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  skipBtn: {
    padding: '10px',
    border: '1px solid #6b4622',
    borderRadius: '8px',
    background: 'none',
    color: '#d99a6a',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '700',
    fontFamily: 'inherit',
    letterSpacing: '0.5px',
    transition: 'border-color .15s ease, color .15s ease',
    '&:hover:not(:disabled)': {
      borderColor: '#d94f2b',
      color: '#fff3e6',
    },
    '&:disabled': {
      opacity: 0.45,
      cursor: 'default',
    },
  },
  // The practice aid. An outlined plate rather than a raised button: it is a
  // teaching tool sitting among the controls of a live round, and it should not
  // read as loud as the bid it sits next to. Turns tomato while playing —
  // urgency is the palette's one accent, and a running playback is the one
  // thing here you might want to interrupt.
  solutionBtn: {
    padding: '10px',
    border: ({ playing }) => `1px solid ${playing ? '#e35c36' : '#6b4622'}`,
    borderRadius: '8px',
    background: 'none',
    color: ({ playing }) => (playing ? '#e35c36' : '#d99a6a'),
    cursor: 'pointer',
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    transition: 'border-color .15s ease, color .15s ease',
    '&:hover': {
      borderColor: '#d94f2b',
      color: '#fff3e6',
    },
  },
  skipStatus: {
    fontFamily: MONO,
    fontSize: '11px',
    lineHeight: 1.5,
    color: '#9c8168',
    textAlign: 'center',
  },
});

function BiddingPanel({ bids, timerSeconds, readyToSkip, players, onReadyToSkip, onUndoReadyToSkip,
  selfId, sandboxMoves, solved, onUndo, onReset, onBid, optimalMoves,
  hasBid, rebidOpen, canBid, bidInputProps, bidInputRef,
  onShowSolution, onStopSolution, solutionProgress, onHint, hint }) {
  const theme = useTheme();
  const urgent = timerSeconds !== null && timerSeconds <= 15;
  const playing = solutionProgress != null;
  const classes = useStyles({ theme, urgent, playing, hintColor: hint?.color });
  const t = useT();

  const readyPlayerIds = readyToSkip ?? [];
  const iAmReady = selfId && readyPlayerIds.includes(selfId);
  const everyoneReady = readyPlayerIds.length > 0 && readyPlayerIds.length >= (players?.length ?? 0);

  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className={classes.container}>
      <div className={classes.label}>{t('bidding.title')}</div>

      {timerSeconds !== null && (
        <div className={classes.timerPlate}>
          <div className={classes.timer}>{mmss(timerSeconds)}</div>
          <div className={classes.timerCaption}>
            {urgent ? t('bidding.underbidWindow') : t('bidding.timeToBid')}
          </div>
        </div>
      )}

      {optimalMoves != null && (
        <div className={classes.optimalPlate}>
          <span className={classes.optimalCaption}>{t('bidding.bestPossible')}</span>
          <span className={classes.optimalValue}>{optimalMoves}</span>
        </div>
      )}

      {hint && (
        <div className={classes.hintPlate}>
          <span className={classes.hintSwatch} />
          <span className={classes.hintRobot}>{robotColorName(hint.color, t)}</span>
          <span className={classes.hintStep}>
            {t('bidding.hintGiven', { step: hint.step, total: hint.total })}
          </span>
        </div>
      )}

      {bids.length > 0 ? (
        <div className={classes.bidList}>
          {bids.map(b => (
            <div key={b.playerId} className={classes.bidItem}>
              <span className={classes.bidName}>{b.username}</span>
              <span className={classes.bidCount}>{b.count}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className={classes.empty}>{t('bidding.noBids')}</div>
      )}

      {onUndo && onBid && (
        <div className={classes.sandboxBar}>
          <button className={classes.sandboxBtn} onClick={onUndo} disabled={!sandboxMoves} title={t('bidding.undo')}>
            ↩
          </button>
          <button className={classes.sandboxBtn} onClick={onReset} title={t('bidding.reset')}>
            ↻
          </button>
          <div className={classes.movesDisplay}>
            <span className={classes.movesLabel}>{t('bidding.bid')}</span>
            <input
              className={classes.bidInput}
              ref={bidInputRef}
              aria-label={t('bidding.bid')}
              {...bidInputProps}
              disabled={!rebidOpen}
            />
          </div>
          <button
            className={classes.bidBtn}
            disabled={!canBid}
            onClick={onBid}
          >
            {hasBid
              ? (rebidOpen ? t('bidding.rebid') : t('bidding.bidPlaced'))
              : t('bidding.bid')}
          </button>
        </div>
      )}

      {onHint && (
        <button className={classes.solutionBtn} onClick={onHint}>
          {hint ? t('bidding.hintAgain') : t('bidding.hint')}
        </button>
      )}

      {onShowSolution && (
        <button
          className={classes.solutionBtn}
          onClick={playing ? onStopSolution : onShowSolution}
        >
          {playing
            ? t('bidding.playingSolution', {
              step: solutionProgress.step, total: solutionProgress.total,
            })
            : t('bidding.showSolution')}
        </button>
      )}

      <div className={classes.skipSection}>
        {iAmReady && !everyoneReady ? (
          <button className={classes.skipBtn} onClick={onUndoReadyToSkip}>
            {t('bidding.cancel')}
          </button>
        ) : (
          <button
            className={classes.skipBtn}
            disabled={iAmReady || everyoneReady}
            onClick={onReadyToSkip}
          >
            {everyoneReady
              ? t('bidding.everyoneReady')
              : iAmReady ? t('bidding.waitingForOthers') : t('bidding.skipBidding')}
          </button>
        )}
        {!everyoneReady && readyPlayerIds.length > 0 && (
          <div className={classes.skipStatus}>
            Ready: {readyPlayerIds.map(id => players?.find(p => p.id === id)?.username ?? id).join(', ')}
          </div>
        )}
      </div>
    </div>
  );
}

export default BiddingPanel;
